import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  Building2,
  FileText,
  ListChecks,
  Send,
  ArrowLeft,
} from "lucide-react";
import { listAssistantPresets } from "@/api/assistant";

const CATEGORIES = [
  { id: "Evaluate", label: "Evaluate", icon: Briefcase },
  { id: "Research", label: "Research", icon: Building2 },
  { id: "Pipeline", label: "Pipeline", icon: ListChecks },
  { id: "CV", label: "CV", icon: FileText },
  { id: "Apply", label: "Apply", icon: Send },
] as const;

export function AssistantPresets({
  onSelect,
}: {
  onSelect: (preset: {
    id: string;
    name: string;
    text: string;
    autorun?: boolean;
  }) => void;
}) {
  const { data } = useQuery({
    queryKey: ["assistant-presets"],
    queryFn: listAssistantPresets,
    staleTime: 60_000,
    retry: false,
  });
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const presets = data?.assistant_presets ?? [];

  const inCategory = useMemo(() => {
    if (!activeCategory) return [];
    return presets.filter(
      (p) => p.visibility !== false && p.category === activeCategory,
    );
  }, [presets, activeCategory]);

  if (activeCategory) {
    return (
      <div className="w-full max-w-2xl">
        <p className="mb-3 text-center text-sm text-muted-foreground">Start with a preset</p>
        <div className="flex flex-wrap justify-center gap-2">
          {inCategory.map((p) => (
            <button
              key={p.id}
              type="button"
              title={p.short_description}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-left text-sm text-foreground transition hover:border-brand/40 hover:bg-brand-soft/40"
              onClick={() =>
                onSelect({
                  id: p.id,
                  name: p.name,
                  text: p.text,
                  autorun: p.autorun,
                })
              }
            >
              {p.name}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="mx-auto mt-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          onClick={() => setActiveCategory(null)}
        >
          <ArrowLeft className="size-3.5" />
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl">
      <p className="mb-3 text-center text-sm text-muted-foreground">Start with a preset</p>
      <div className="flex flex-wrap justify-center gap-2">
        {CATEGORIES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:border-brand/40 hover:bg-brand-soft/40"
            onClick={() => setActiveCategory(id)}
          >
            <Icon className="size-3.5 text-muted-foreground" />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
